/**
 * Sticky notes — Excalidraw has none, so a sticky is a filled square
 * rectangle (`customData.sticky`) with a bound, centred text label. The text
 * auto-fits: the runtime (stickyFit.ts) picks the largest font size from
 * STICKY_FONT_SIZES whose wrapped text fits inside the square.
 */

import { FONT, labelledShape, type El } from './elements';

export type StickyColor = 'yellow' | 'orange' | 'green' | 'blue' | 'pink' | 'purple' | 'grey';

/** Miro's sticky palette (light tints, dark text). */
export const STICKY_COLORS: Record<StickyColor, string> = {
  yellow: '#fff9b1',
  orange: '#ffc68a',
  green: '#d5f692',
  blue: '#a6ccf5',
  pink: '#ffcee0',
  purple: '#d7c0f2',
  grey: '#e6e6e6',
};
export const STICKY_ORDER: StickyColor[] = ['yellow', 'orange', 'green', 'blue', 'pink', 'purple', 'grey'];
export const STICKY_SIZE = 200;
export const STICKY_TEXT_COLOR = '#1e1e1e';
/** Candidate font sizes, largest first. */
export const STICKY_FONT_SIZES = [48, 40, 36, 32, 28, 24, 20, 18, 16, 14, 12, 10, 8];
/** Inner margin (each side) the fitted text must stay within. */
export const STICKY_PADDING = 14;

export interface StickyData {
  sticky: true;
  color: StickyColor;
  size: number;
}

export function isStickyContainer(el: { type: string; customData?: Record<string, unknown> } | null | undefined): boolean {
  return !!el && el.type === 'rectangle' && !!el.customData && (el.customData as { sticky?: unknown }).sticky === true;
}

export interface StickyOptions {
  id: string;
  /** Centre of the note. */
  cx: number;
  cy: number;
  color?: StickyColor;
  text?: string;
  size?: number;
  fontFamily?: number;
  fontSize?: number;
  frameId?: string | null;
  groupIds?: string[];
}

/** [container, text] for a sticky note centred at (cx, cy). */
export function stickyElements(o: StickyOptions): El[] {
  const size = o.size ?? STICKY_SIZE;
  const color = o.color ?? 'yellow';
  const data: StickyData = { sticky: true, color, size };
  return labelledShape('rectangle', o.id, {
    x: o.cx - size / 2,
    y: o.cy - size / 2,
    width: size,
    height: size,
    backgroundColor: STICKY_COLORS[color],
    strokeColor: 'transparent',
    fillStyle: 'solid',
    strokeWidth: 1,
    roughness: 0,
    roundness: null,
    frameId: o.frameId ?? null,
    groupIds: o.groupIds,
    customData: data as unknown as Record<string, unknown>,
    label: o.text ?? '',
    fontSize: o.fontSize ?? 24,
    fontFamily: o.fontFamily ?? FONT.Brand,
  }).map((e) => (e.type === 'text' ? { ...e, strokeColor: STICKY_TEXT_COLOR } : e));
}

/**
 * Pick the largest candidate size whose measured text fits the note.
 * `measure(size)` returns the wrapped text block's {width, height} at that size.
 */
export function pickStickyFontSize(
  measure: (fontSize: number) => { width: number; height: number },
  noteSize: number,
  sizes: readonly number[] = STICKY_FONT_SIZES,
  padding = STICKY_PADDING,
): number {
  const inner = noteSize - 2 * padding;
  for (const s of sizes) {
    const m = measure(s);
    if (m.height <= inner && m.width <= noteSize - 10 + 0.5) return s;
  }
  return sizes[sizes.length - 1];
}
